import React, { useState, useRef } from "react";
import { socialLinks } from "../../data";
import { MailIcon } from "@heroicons/react/solid";

export default function Contact() {
  const formRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    const formData = new FormData(formRef.current);
    const data = Object.fromEntries(formData.entries());

    fetch("https://0z5hohe6ek.execute-api.us-east-1.amazonaws.com/Dev/submit", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    })
      .then((res) => {
        if (res.ok) {
          setSubmitted(true);
          if (formRef.current) formRef.current.reset();
        } else {
          setErrorMessage("Something went wrong submitting your message. Please try again or email directly.");
        }
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setErrorMessage("Network error. Please try again or reach out via email.");
        setLoading(false);
      });
  };

  return (
    <section id="contact" className="text-gray-300 bg-gray-900 body-font relative py-20 border-t border-gray-800">
      <div className="container px-5 mx-auto max-w-4xl">
        <div className="text-center mb-12">
          <MailIcon className="w-10 h-10 inline-block mb-4 text-green-400" />
          <h2 className="text-white sm:text-4xl text-3xl font-bold title-font mb-4">
            Get In Touch
          </h2>
          <p className="text-gray-400 text-lg leading-relaxed max-w-2xl mx-auto">
            Have a project in mind, need custom web engineering, or want to collaborate? Send a message below or connect directly through my professional channels.
          </p>

          {/* Social Profiles & External Links */}
          <div className="flex justify-center gap-4 mt-6">
            {socialLinks.map((social) => (
              <a
                key={social.name}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-gray-800 hover:bg-gray-700 text-gray-200 text-sm font-medium rounded-lg border border-gray-700 hover:border-green-500 transition-all duration-200"
              >
                {social.name}
              </a>
            ))}
          </div>
        </div>

        <div className="bg-gray-950 p-8 sm:p-10 rounded-2xl border border-gray-800 shadow-xl max-w-2xl mx-auto">
          {submitted ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-4 text-2xl font-bold">
                ✓
              </div>
              <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
              <p className="text-gray-400 mb-6">
                Thank you for reaching out. I have received your message and will respond promptly.
              </p>
              <button
                onClick={() => setSubmitted(false)}
                className="px-6 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg text-sm transition-colors"
              >
                Send Another Message
              </button>
            </div>
          ) : (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              name="contact-form"
              id="contact-form"
              className="flex flex-col space-y-5"
            >
              {errorMessage && (
                <div className="p-4 bg-red-900/40 border border-red-500/50 rounded-lg text-red-300 text-sm">
                  {errorMessage}
                </div>
              )}

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-1">
                  Your Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  placeholder="Jane Doe"
                  className="w-full bg-gray-900 rounded-lg border border-gray-700 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-base outline-none text-gray-100 py-2.5 px-4 leading-8 transition-colors duration-200"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-1">
                  Your Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  placeholder="jane@example.com"
                  className="w-full bg-gray-900 rounded-lg border border-gray-700 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-base outline-none text-gray-100 py-2.5 px-4 leading-8 transition-colors duration-200"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-1">
                  Project Details / Message <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows="4"
                  placeholder="Tell me about your project, timeline, and goals..."
                  className="w-full bg-gray-900 rounded-lg border border-gray-700 focus:border-green-500 focus:ring-1 focus:ring-green-500 text-base outline-none text-gray-100 py-2.5 px-4 resize-none leading-6 transition-colors duration-200"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full text-white bg-green-500 hover:bg-green-600 focus:outline-none font-semibold rounded-lg text-lg py-3 px-6 shadow-lg hover:shadow-green-500/20 transition-all duration-200 disabled:opacity-50"
              >
                {loading ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>

        <footer className="mt-16 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} MarkNYCweb. All rights reserved. New York City, NY.</p>
        </footer>
      </div>
    </section>
  );
}